"use client";

import { c as _c } from "react/compiler-runtime";
import { cva } from "class-variance-authority";
import { Collapsible, CollapsibleContent } from "fumadocs-ui/components/ui/collapsible";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { usePathname } from "next/navigation";
import { type SyntheticEvent, useEffect, useState, useTransition } from "react";
import { cn } from "../lib/cn";
import { buttonVariants } from "./ui/button";
const rateButtonVariants = cva("inline-flex items-center gap-2 px-3 py-2 rounded-full font-medium border text-sm [&_svg]:size-4 disabled:cursor-not-allowed", {
  variants: {
    active: {
      true: "bg-fd-accent text-fd-accent-foreground [&_svg]:fill-current",
      false: "text-fd-muted-foreground"
    }
  }
});
export interface Feedback {
  opinion: "good" | "bad";
  url?: string;
  message: string;
}
export interface ActionResponse {
  githubUrl?: string;
}
interface Result extends Feedback {
  response?: ActionResponse;
}
export function Feedback(t0) {
  const $ = _c(40);
  const {
    onRateAction
  } = t0;
  const url = usePathname();
  const [previous, setPrevious] = useState(null);
  const [opinion, setOpinion] = useState(null);
  const [message, setMessage] = useState("");
  const [isPending, startTransition] = useTransition();
  let t1;
  let t2;
  if ($[0] !== url) {
    t1 = () => {
      const item = localStorage.getItem(`docs-feedback-${url}`);
      if (item === null) {
        return;
      }
      setPrevious(JSON.parse(item) as Result);
    };
    t2 = [url];
    $[0] = url;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== previous || $[4] !== url) {
    t3 = () => {
      const key = `docs-feedback-${url}`;
      if (previous) {
        localStorage.setItem(key, JSON.stringify(previous));
      } else {
        localStorage.removeItem(key);
      }
    };
    t4 = [previous, url];
    $[3] = previous;
    $[4] = url;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== message || $[8] !== onRateAction || $[9] !== opinion || $[10] !== url) {
    t5 = function submit(e) {
      if (opinion === null) {
        return;
      }
      startTransition(async () => {
        const feedback = {
          opinion,
          message
        };
        onRateAction(url, feedback).then(response => {
          setPrevious({
            response,
            ...feedback
          });
          setMessage("");
          setOpinion(null);
        });
      });
      e?.preventDefault();
    };
    $[7] = message;
    $[8] = onRateAction;
    $[9] = opinion;
    $[10] = url;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const submit = t5;
  const activeOpinion = previous?.opinion ?? opinion;
  const t6 = opinion !== null || previous !== null;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = v => {
      if (!v) {
        setOpinion(null);
      }
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="text-sm font-medium pe-2">How is this guide?</p>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = previous !== null;
  let t10;
  if ($[14] !== activeOpinion) {
    t10 = cn(rateButtonVariants({
      active: activeOpinion === "good"
    }));
    $[14] = activeOpinion;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setOpinion("good");
    };
    t12 = <ThumbsUp />;
    $[16] = t11;
    $[17] = t12;
  } else {
    t11 = $[16];
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t10 || $[19] !== t9) {
    t13 = <button disabled={t9} className={t10} onClick={t11}>{t12}Good</button>;
    $[18] = t10;
    $[19] = t9;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = previous !== null;
  let t15;
  if ($[21] !== activeOpinion) {
    t15 = cn(rateButtonVariants({
      active: activeOpinion === "bad"
    }));
    $[21] = activeOpinion;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  let t17;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      setOpinion("bad");
    };
    t17 = <ThumbsDown />;
    $[23] = t16;
    $[24] = t17;
  } else {
    t16 = $[23];
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t14 || $[26] !== t15) {
    t18 = <button disabled={t14} className={t15} onClick={t16}>{t17}Bad</button>;
    $[25] = t14;
    $[26] = t15;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== t13 || $[29] !== t18) {
    t19 = <div className="flex flex-row items-center gap-2">{t8}{t13}{t18}</div>;
    $[28] = t13;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== isPending || $[32] !== message || $[33] !== previous || $[34] !== submit) {
    t20 = <CollapsibleContent className="mt-3">{previous ? <div className="px-3 py-6 flex flex-col items-center gap-3 bg-fd-card text-fd-muted-foreground text-sm text-center rounded-xl"><p>Thank you for your feedback!</p><div className="flex flex-row items-center gap-2"><a href={previous.response?.githubUrl} rel="noreferrer noopener" target="_blank" className={cn(buttonVariants({
            color: "primary"
          }), "text-xs")}>View on GitHub</a><button className={cn(buttonVariants({
            color: "secondary"
          }), "text-xs")} onClick={() => {
            setOpinion(previous.opinion);
            setPrevious(null);
          }}>Submit Again</button></div></div> : <form className="flex flex-col gap-3" onSubmit={submit}><textarea autoFocus={true} required={true} value={message} onChange={e_0 => setMessage(e_0.target.value)} className="border rounded-lg bg-fd-secondary text-fd-secondary-foreground p-3 resize-none focus-visible:outline-none placeholder:text-fd-muted-foreground" placeholder="Leave your feedback..." onKeyDown={e_1 => {
          if (!e_1.shiftKey && e_1.key === "Enter") {
            submit(e_1);
          }
        }} /><button type="submit" className={cn(buttonVariants({
          color: "outline"
        }), "w-fit px-3")} disabled={isPending}>Submit</button></form>}</CollapsibleContent>;
    $[31] = isPending;
    $[32] = message;
    $[33] = previous;
    $[34] = submit;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t19 || $[37] !== t20 || $[38] !== t6) {
    t21 = <Collapsible open={t6} onOpenChange={t7} className="border-y py-3">{t19}{t20}</Collapsible>;
    $[36] = t19;
    $[37] = t20;
    $[38] = t6;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  return t21;
}